import { Controller } from "@hotwired/stimulus";

// Internal debug tool, ported from the old Vue debug_panel.vue + its 6
// debug_tab_*.vue children (deleted along with the step1 Vue/Vuex mount --
// see packs/desktop/step1.js). Rebuilt on plain Stimulus rather than
// reintroducing Vue for one page; the actual debug data still comes from
// the same real, unmodified F-ENGINE endpoint the old panel called
// (SearchChannel#load_debug_info) -- this controller only replaces how
// that response gets fetched and rendered, not what it contains.
//
// Cards to hover over aren't present at connect() time (step1's results
// list is fetched and injected asynchronously by step1-results, often
// after this controller has already connected) and get re-fetched/
// replaced on every filter change, so a direct per-card listener would
// miss cards added later and leak listeners on cards that get replaced.
// Delegates a single mouseover listener on the document instead (mouseover
// bubbles, mouseenter doesn't), matching the old code's practical effect
// (attach once, work for every card that ever appears) without needing a
// MutationObserver.
export default class extends Controller {
  static targets = ["openButton", "panel", "resizeHandle", "tabList", "content", "uuidLabel", "falcoLink"];
  static values = { searchId: String, debugWindow: Boolean };

  connect() {
    this.cableUrl = document.querySelector('meta[name="action-cable-http-url"]')?.content || "";
    this.debugUUID = null;
    this.debugData = null;
    this.activeTabKey = null;
    this.resizing = false;

    this.tabs = [
      { key: "price", title: "Price", canShow: (d) => !!(d?.itinerary?.data?.attributes?.price_list && d?.itinerary?.data?.attributes?.unit_price_list), render: this.renderPrice.bind(this) },
      { key: "segments", title: "Segments", canShow: (d) => !!d?.itinerary, render: this.renderSegments.bind(this) },
      { key: "ancillaries", title: "Ancillaries", canShow: (d) => !!d?.itinerary?.data?.attributes?.ancillaries, render: this.renderAncillaries.bind(this) },
      { key: "hotel", title: "Hotel", canShow: (d) => !!d?.hotel_id, render: this.renderHotel.bind(this) },
      { key: "presentation", title: "Presentationrules", canShow: (d) => !!d?.presentationrules, render: this.renderPresentation.bind(this) },
      { key: "raw", title: "Raw", canShow: (d) => !!d, render: this.renderRaw.bind(this) },
    ];

    this.boundMouseOver = this.handleMouseOver.bind(this);
    document.addEventListener("mouseover", this.boundMouseOver);
    this.boundMouseMove = this.handleResizeMove.bind(this);
    this.boundMouseUp = this.handleResizeUp.bind(this);

    this.falcoLinkTarget.href = `https://admin.travex.se/logs/searches/${this.searchIdValue}`;
  }

  disconnect() {
    document.removeEventListener("mouseover", this.boundMouseOver);
    document.removeEventListener("mousemove", this.boundMouseMove);
    document.removeEventListener("mouseup", this.boundMouseUp);
  }

  open() {
    this.panelTarget.classList.remove("hidden");
    this.panelTarget.classList.add("flex");
    this.openButtonTarget.classList.add("hidden");
  }

  close() {
    this.panelTarget.classList.add("hidden");
    this.panelTarget.classList.remove("flex");
    this.openButtonTarget.classList.remove("hidden");
  }

  startResize(event) {
    this.resizing = true;
    document.addEventListener("mousemove", this.boundMouseMove);
    document.addEventListener("mouseup", this.boundMouseUp);
    document.body.classList.add("select-none");
  }

  handleResizeMove(event) {
    if (!this.resizing) return;
    const newHeight = window.innerHeight - event.clientY;
    if (newHeight > 50 && newHeight < 700) {
      this.panelTarget.style.height = `${newHeight}px`;
    }
  }

  handleResizeUp() {
    this.resizing = false;
    document.removeEventListener("mousemove", this.boundMouseMove);
    document.removeEventListener("mouseup", this.boundMouseUp);
    document.body.classList.remove("select-none");
  }

  handleMouseOver(event) {
    const card = event.target.closest("[data-debug-panel-uuid]");
    if (!card) return;
    const uuid = card.dataset.debugPanelUuid;
    const altUuid = card.dataset.debugPanelAltUuid || null;
    this.loadItinerary(uuid, altUuid);
  }

  loadItinerary(uuid, altUuid) {
    if (this.debugUUID === uuid || !this.cableUrl) return;
    this.debugUUID = uuid;

    const url = `${this.cableUrl}/SearchChannel/load_debug_info?search_id=${this.searchIdValue}`;
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ uuid, alt_uuid: altUuid, debug_window: this.debugWindowValue }),
    })
      .then((response) => response.json())
      .then((data) => {
        // Matches the old channel's packet shape ({action, items}) --
        // load_debug_info's real payload is under .items when wrapped
        // that way, or the bare object if F-ENGINE returns it directly.
        this.debugData = data.items || data.data || data;
        this.renderTabs();
      })
      .catch(() => {
        this.contentTarget.innerHTML = '<p class="p-4 text-danger">Failed to load debug info.</p>';
      });
  }

  renderTabs() {
    const visibleTabs = this.tabs.filter((tab) => tab.canShow(this.debugData));
    if (!visibleTabs.some((tab) => tab.key === this.activeTabKey)) {
      this.activeTabKey = visibleTabs[0]?.key || null;
    }

    this.tabListTarget.innerHTML = visibleTabs
      .map(
        (tab) => `
        <li>
          <button type="button" data-tab-key="${tab.key}" data-action="click->debug-panel#selectTab"
                  class="rounded-t border border-b-0 border-gray-500 px-3 py-1 text-xs ${tab.key === this.activeTabKey ? "-mb-px bg-white font-semibold" : "bg-gray-200 text-gray-600"}">
            ${tab.title}
          </button>
        </li>`
      )
      .join("");

    this.uuidLabelTarget.textContent = this.debugData?.uuid ? `uuid: ${this.debugData.uuid}` : "";

    const activeTab = visibleTabs.find((tab) => tab.key === this.activeTabKey);
    this.contentTarget.innerHTML = activeTab ? activeTab.render(this.debugData) : '<p class="p-4 text-gray-500">No debug data for this itinerary.</p>';
  }

  selectTab(event) {
    this.activeTabKey = event.currentTarget.dataset.tabKey;
    this.renderTabs();
  }

  // -- Tab renderers, one per debug_tab_*.vue this replaces --

  renderPrice(data) {
    const buildBlock = (it) => {
      const attrs = it?.data?.attributes;
      if (!attrs) return "";
      const priceRows = (attrs.price_list || [])
        .map(
          (p) => `
          <tr class="border-b border-gray-400">
            <td class="px-2 py-0.5">${p.title ?? ""}</td>
            <td class="px-2 py-0.5">${p.price_type ?? ""}</td>
            <td class="px-2 py-0.5">${p.net_price ?? ""} ${p.currency ?? ""}${p.ex_net_price ? ` (${p.ex_net_price} ${attrs.currency ?? ""})` : ""}</td>
            <td class="px-2 py-0.5">${p.ex_rate ?? ""}</td>
            <td class="px-2 py-0.5">${p.tax ? `${p.tax} ${p.currency ?? ""}` : ""}</td>
            <td class="px-2 py-0.5">${p.sales_price ? `${p.sales_price} ${p.currency ?? ""}` : ""}</td>
            <td class="px-2 py-0.5">${p.profit ? `${p.profit} ${p.currency ?? ""}` : ""}</td>
            <td class="px-2 py-0.5">${p.pricerule ?? ""}</td>
            <td class="px-2 py-0.5">${p.searchrule ?? ""}</td>
          </tr>`
        )
        .join("");
      const unitRows = (attrs.unit_price_list || [])
        .map(
          (u) => `
          <tr class="border-b border-gray-400">
            <td class="px-2 py-0.5">${u.title ?? ""}</td>
            <td class="px-2 py-0.5">${u.fare ?? ""} ${u.currency ?? ""}${u.ex_net_price ? ` (${u.ex_net_price})` : ""}</td>
            <td class="px-2 py-0.5">${u.tax ?? ""} ${u.currency ?? ""}</td>
            <td class="px-2 py-0.5">${u.net_price ?? ""} ${u.currency ?? ""}</td>
            <td class="px-2 py-0.5">${u.sales_price ?? ""} ${u.currency ?? ""}</td>
            <td class="px-2 py-0.5">${u.profit ?? ""} ${u.currency ?? ""}</td>
          </tr>`
        )
        .join("");

      return `
        <div class="mb-4 flex gap-4">
          <table class="w-3/5 border-collapse text-xs">
            <thead><tr class="border-b border-gray-500 text-left"><th class="px-2 py-0.5">Description</th><th class="px-2 py-0.5">Type</th><th class="px-2 py-0.5">Cost</th><th class="px-2 py-0.5">Ex. rate</th><th class="px-2 py-0.5">Vat</th><th class="px-2 py-0.5">Sales</th><th class="px-2 py-0.5">Profit</th><th class="px-2 py-0.5">Pricerule</th><th class="px-2 py-0.5">Searchrule</th></tr></thead>
            <tbody>${priceRows}</tbody>
          </table>
          <table class="w-2/5 border-collapse text-xs">
            <thead><tr class="border-b border-gray-500 text-left"><th class="px-2 py-0.5">Passenger type</th><th class="px-2 py-0.5">Fare</th><th class="px-2 py-0.5">Tax</th><th class="px-2 py-0.5">Cost</th><th class="px-2 py-0.5">Sales</th><th class="px-2 py-0.5">Profit</th></tr></thead>
            <tbody>${unitRows}</tbody>
          </table>
        </div>`;
    };

    return buildBlock(data.itinerary) + (data.itinerary2 ? `<div class="my-2 h-2 bg-gray-300"></div>${buildBlock(data.itinerary2)}` : "");
  }

  renderSegments(data) {
    const legs = data?.itinerary?.data?.attributes?.segments || [];
    const rows = legs
      .flat()
      .map(
        (s) => `
        <tr class="border-b border-gray-400">
          <td class="px-1.5 py-0.5 text-center">${s.index ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.departure_airport ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.departure_time ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.arrival_airport ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.arrival_time ?? ""}</td>
          <td class="px-1.5 py-0.5 text-center">${s.leg_index ?? ""}</td>
          <td class="px-1.5 py-0.5 text-center">${s.part_index ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.carrier ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.flightnumber ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.operating_carrier ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.operating_flightnumber ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.flight_class ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.cabin ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.farebasis ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.brand_name ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.equipment_type ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.departure_terminal ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.arrival_terminal ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.flight_time ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.travel_time ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.meal_code ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.ndc ?? ""}</td>
          <td class="px-1.5 py-0.5">${s.md5key ?? ""}</td>
        </tr>`
      )
      .join("");

    return `
      <table class="w-full border-collapse whitespace-nowrap text-xs">
        <thead><tr class="border-b border-gray-500 text-left">
          <th class="px-1.5 py-0.5">#</th><th class="px-1.5 py-0.5">Dep. airport</th><th class="px-1.5 py-0.5">Dep. time</th><th class="px-1.5 py-0.5">Arr. airport</th><th class="px-1.5 py-0.5">Arr. time</th>
          <th class="px-1.5 py-0.5">Leg</th><th class="px-1.5 py-0.5">Part</th><th class="px-1.5 py-0.5">Carrier</th><th class="px-1.5 py-0.5">Flightno.</th><th class="px-1.5 py-0.5">Op. carrier</th>
          <th class="px-1.5 py-0.5">Op. flightno.</th><th class="px-1.5 py-0.5">Class</th><th class="px-1.5 py-0.5">Cabin</th><th class="px-1.5 py-0.5">Farebasis</th><th class="px-1.5 py-0.5">Brand</th>
          <th class="px-1.5 py-0.5">Equipment</th><th class="px-1.5 py-0.5">Dep. term.</th><th class="px-1.5 py-0.5">Arr. term.</th><th class="px-1.5 py-0.5">Flight time</th><th class="px-1.5 py-0.5">Travel time</th>
          <th class="px-1.5 py-0.5">Meal</th><th class="px-1.5 py-0.5">Ndc segment</th><th class="px-1.5 py-0.5">MD5</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
  }

  // Where a bag's price actually came from.
  //
  // F-ENGINE does not stamp a source onto the ancillary, so this reads the
  // fingerprint its own pricing paths leave behind:
  //
  //   FakeBags (app/services/fake_bags.rb) prices a bag from the
  //   ndc_bag_prices table -- what F-ADMIN lists as "NDC bag prices" and
  //   "Bag prices", the two being the same table split on its `ndc` flag --
  //   and writes a fixed description, "1 BAG" or "1 CARRYON BAG", with
  //   passenger_code "ALL". Nothing else produces those exact strings.
  //   Its airport_buyable branch prices from the same record's
  //   airport_bag_price instead.
  //
  //   A bag that came down with the fare carries the airline's own wording
  //   ("PRE PAID BAGGAGE" and so on -- see the shape documented at the top
  //   of itinerary_baggage.rb).
  //
  // The NDC/non-NDC half of the label is the segment's own ndc flag, which
  // is what FakeBags#price passes to the lookup, so it picks the same list
  // of the two that F-ADMIN shows.
  ancillaryPriceSource(ancillary, isNdc) {
    const description = String(ancillary.description || "").trim().toUpperCase();
    const fromBagPriceTable = description === "1 BAG" || description === "1 CARRYON BAG";

    if (ancillary.airport_buyable) {
      return { label: "F-ADMIN airport bag price", basis: "airport_buyable = true" };
    }

    if (fromBagPriceTable) {
      return {
        label: isNdc ? "F-ADMIN \u2192 NDC bag prices" : "F-ADMIN \u2192 Bag prices",
        basis: `description "${ancillary.description}" + segment ndc=${isNdc}`
      };
    }

    return { label: "GDS / search result", basis: `airline description "${ancillary.description ?? ""}"` };
  }

  renderAncillaries(data) {
    const attributes = data?.itinerary?.data?.attributes || {};
    const collections = attributes.baggage_ancillaries || {};

    // attributes.segments is a list of LEGS, each a list of segments -- not
    // a flat list, which is what this first assumed. Checking the outer
    // array found no .ndc on anything and quietly labelled every NDC
    // itinerary as non-NDC, i.e. named the wrong F-ADMIN list. Flattened
    // one level, tolerating either shape. Any NDC segment makes this an NDC
    // itinerary for the purposes of which bag-price list was consulted.
    const allSegments = (attributes.segments || []).flat
      ? (attributes.segments || []).flat()
      : [].concat.apply([], attributes.segments || []);
    const isNdc = allSegments.some((segment) => segment && segment.ndc);

    const rows = [];
    Object.values(collections).forEach((paxBagsList) => {
      (paxBagsList || []).forEach((paxBags) => {
        (paxBags.ancillaries || []).forEach((ancillary) => {
          const pricerule = ancillary.pricerule_id ? ancillary.pricerule_id[(ancillary.key || "").toLowerCase()] : "";
          const source = this.ancillaryPriceSource(ancillary, isNdc);
          const markup = Number(ancillary.markup || 0);
          rows.push(`
            <tr class="border-b border-gray-400">
              <td class="px-2 py-0.5">${paxBags.pax_type ?? ""} ${paxBags.pax_number ?? ""}</td>
              <td class="px-2 py-0.5">${paxBags.leg_number ?? ""}</td>
              <td class="px-2 py-0.5">${ancillary.price ?? ""}</td>
              <td class="px-2 py-0.5">${ancillary.net_price ?? ""}</td>
              <td class="px-2 py-0.5 ${markup ? "font-semibold" : "text-gray-500"}">${ancillary.markup ?? ""}</td>
              <td class="px-2 py-0.5 font-semibold" title="${this.escapeHtml(source.basis)}">${this.escapeHtml(source.label)}</td>
              <td class="px-2 py-0.5 text-gray-600">${this.escapeHtml(source.basis)}</td>
              <td class="px-2 py-0.5">${ancillary.id ?? ""}</td>
              <td class="px-2 py-0.5">${pricerule ?? ""}</td>
            </tr>`);
        });
      });
    });

    return `
      <p class="mb-2 text-gray-600">
        Sales = what the customer pays, per passenger. Cost = what we pay.
        Source is inferred from how F-ENGINE priced the bag (hover it for the
        evidence); a Pricerule id means a rule from the database adjusted the
        sales price on top of that source.
      </p>
      <table class="w-full border-collapse text-xs">
        <thead><tr class="border-b border-gray-500 text-left"><th class="px-2 py-0.5">Passenger</th><th class="px-2 py-0.5">Leg</th><th class="px-2 py-0.5">Sales Price</th><th class="px-2 py-0.5">Cost Price</th><th class="px-2 py-0.5">Markup</th><th class="px-2 py-0.5">Price source</th><th class="px-2 py-0.5">Why</th><th class="px-2 py-0.5">Description</th><th class="px-2 py-0.5">Pricerule</th></tr></thead>
        <tbody>${rows.join("")}</tbody>
      </table>`;
  }

  renderHotel(data) {
    return `<p class="mb-2 font-semibold">Debug information</p><pre class="whitespace-pre-wrap break-all">${this.escapeHtml(JSON.stringify(data, null, 2))}</pre>`;
  }

  renderPresentation(data) {
    const rulesRows = Object.entries(data.presentationrules || {})
      .map(([k, v]) => `<tr><td class="pr-3 py-0.5 align-top font-semibold">${k}</td><td class="py-0.5">${this.escapeHtml(String(v))}</td></tr>`)
      .join("");
    const template = data?.itinerary?.data?.attributes?.template;
    const templateRows = template
      ? Object.entries(template)
          .map(([k, v]) => `<tr><td class="pr-3 py-0.5 align-top font-semibold">${k}</td><td class="py-0.5">${this.escapeHtml(String(v))}</td></tr>`)
          .join("")
      : null;

    return `
      <div class="flex gap-8 p-2">
        <div>
          <h4 class="mb-1 text-sm font-semibold">Presentationrules</h4>
          <table class="text-xs"><tbody>${rulesRows}</tbody></table>
        </div>
        ${templateRows ? `<div><h4 class="mb-1 text-sm font-semibold">Itinerary Presentationrules</h4><table class="text-xs"><tbody>${templateRows}</tbody></table></div>` : ""}
      </div>`;
  }

  renderRaw(data) {
    return `<pre class="whitespace-pre-wrap break-all p-2">${this.escapeHtml(JSON.stringify(data, null, 2))}</pre>`;
  }

  escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }
}
